import React, { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Reveal from "../components/Reveal";
import { SHOP } from "../lib/shop";
import { Star, Loader2, Link as LinkIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

export default function Reviews() {
  const { user } = useAuth();
  const [reviews, setReviews] = useState(null);
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get("/reviews").then((r) => setReviews(r.data)).catch(() => setReviews([]));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSubmitting(true);
    try {
      await api.post("/reviews", { rating, text });
      toast.success("Thank you! Your review will appear once approved.");
      setText("");
      setRating(5);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="reviews-page" className="pt-32 pb-24 max-w-6xl mx-auto px-5 sm:px-8 min-h-screen">
      <Reveal>
        <div className="text-center">
          <div className="label-caps mb-3">Customer Love</div>
          <h1 className="font-display text-7xl sm:text-8xl tracking-tighter text-[#D4AF37]">{SHOP.rating}</h1>
          <div className="flex justify-center gap-1 mt-3">
            {[...Array(5)].map((_, i) => <Star key={i} className="w-6 h-6 fill-[#D4AF37] text-[#D4AF37]" />)}
          </div>
          <p className="text-gray-400 mt-3">Based on {SHOP.reviewCount}+ Google reviews</p>
        </div>
      </Reveal>

      {reviews === null ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" /></div>
      ) : (
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6">
          {reviews.map((r, i) => (
            <Reveal key={r.id} delay={(i % 2) * 0.08}>
              <div className="bg-[#141414] border border-white/5 rounded-2xl p-8 h-full">
                <div className="flex gap-1 mb-4">
                  {[...Array(r.rating)].map((_, j) => <Star key={j} className="w-4 h-4 fill-[#D4AF37] text-[#D4AF37]" />)}
                </div>
                <p className="text-gray-300 italic leading-relaxed">"{r.text}"</p>
                <div className="mt-5 label-caps">{r.author}</div>
              </div>
            </Reveal>
          ))}
        </div>
      )}

      <Reveal>
        <div className="mt-16 bg-[#141414] border border-white/10 rounded-2xl p-8 max-w-2xl mx-auto">
          <h3 className="font-display text-3xl mb-5">Leave a Review</h3>
          {user ? (
            <form onSubmit={submit} data-testid="review-form" className="space-y-5">
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    type="button"
                    key={n}
                    onClick={() => setRating(n)}
                    data-testid={`review-star-${n}`}
                    className="transition-transform duration-200 hover:scale-110"
                  >
                    <Star className={`w-8 h-8 ${n <= rating ? "fill-[#D4AF37] text-[#D4AF37]" : "text-gray-600"}`} />
                  </button>
                ))}
              </div>
              <textarea
                data-testid="review-text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={4}
                placeholder="Tell us about your experience..."
                className="w-full bg-[#0A0A0A] border border-white/10 rounded-xl p-4 text-white placeholder-gray-500 focus:border-[#D4AF37] focus:ring-0"
              />
              <button
                data-testid="review-submit"
                disabled={submitting}
                className="px-8 py-3 rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#F3E5AB] transition-colors duration-300 disabled:opacity-60"
              >
                {submitting ? "Submitting..." : "Submit Review"}
              </button>
            </form>
          ) : (
            <p className="text-gray-400">
              <Link to="/login" className="text-[#D4AF37] inline-flex items-center gap-1">
                <LinkIcon className="w-4 h-4" /> Sign in
              </Link>{" "}
              to leave a review.
            </p>
          )}
        </div>
      </Reveal>
    </div>
  );
}
